import React, { useState, useContext } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Box,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  Home,
  Inventory,
  Storefront,
  ExpandLess,
  ExpandMore,
  People,
  Description,
  LocalOffer,
  AccountBalance,
} from "@mui/icons-material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { AppContext } from "../../../context/AppContext";

const SidebarMenu = ({
  open,
  setOpen,
  submenuOpen,
  setSubmenuOpen,
  navigate,
}) => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const [accountingOpen, setAccountingOpen] = useState(false);

  const { user } = useContext(AppContext);

  const isSuperuser = user.isSuperuser;
  const isAdmin = user.isAdmin;
  const isVendedor = user.isVendedor;

  const hasAccess = isSuperuser || isAdmin || isVendedor;
  const canSeeAccounting = isSuperuser || isAdmin;

  const drawerWidth = open ? 224 : 82;

  const colors = {
    base: "#1565C0",
    hover: "#0d47a1",
    active: "#0d47a1",
    submenu: "#0d47a1",
    submenuHover: "#0b3c8a",
    border: "rgba(255,255,255,0.16)",
    text: "#FFFFFF",
    muted: "#FFFFFF",
    soft: "#FFFFFF",
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) setOpen(false);
  };

  const handleToggleInventory = () => {
    setSubmenuOpen(!submenuOpen);
  };

  const handleToggleAccounting = () => {
    setAccountingOpen(!accountingOpen);
  };

  const MainText = ({ label }) => {
    if (!open && !isMobile) {
      return (
        <Typography
          sx={{
            color: colors.text,
            fontSize: "0.64rem",
            fontWeight: 500,
            lineHeight: 1.05,
            textAlign: "center",
            width: "100%",
            mt: 0.55,
            px: 0.3,
            letterSpacing: "-0.01em",
          }}
        >
          {label}
        </Typography>
      );
    }

    return (
      <ListItemText
        primary={label}
        sx={{
          ml: 1.6,
          color: colors.text,
          whiteSpace: "nowrap",
          "& .MuiTypography-root": {
            fontSize: isMobile ? "0.96rem" : "0.92rem",
            fontWeight: 500,
            letterSpacing: "-0.01em",
          },
        }}
      />
    );
  };

  const SubText = ({ label }) => {
    return (
      <Typography
        sx={{
          color: colors.muted,
          fontSize: open || isMobile ? "0.86rem" : "0.63rem",
          fontWeight: 500,
          lineHeight: open || isMobile ? 1.25 : 1.08,
          textAlign: open || isMobile ? "left" : "center",
          width: "100%",
          whiteSpace: open || isMobile ? "nowrap" : "normal",
          letterSpacing: "-0.01em",
        }}
      >
        {label}
      </Typography>
    );
  };

  const MainItem = ({ icon, label, onClick, active, expandable, expanded }) => {
    return (
      <ListItem disablePadding sx={{ px: 1, mb: 0.25 }}>
        <ListItemButton
          onClick={onClick}
          sx={{
            minHeight: open || isMobile ? 46 : 58,
            px: open || isMobile ? 1.4 : 0.8,
            py: open || isMobile ? 0.75 : 0.85,
            borderRadius: 2,
            flexDirection: open || isMobile ? "row" : "column",
            alignItems: "center",
            justifyContent: open || isMobile ? "flex-start" : "center",
            bgcolor: active ? colors.active : "transparent",
            color: colors.text,
            transition: "background-color .18s ease",
            "&:hover": {
              bgcolor: colors.hover,
            },
          }}
        >
          {React.cloneElement(icon, {
            sx: {
              color: colors.text,
              fontSize: open || isMobile ? 22 : 23,
              flexShrink: 0,
            },
          })}

          <MainText label={label} />

          {(open || isMobile) && expandable && (
            <Box
              sx={{
                ml: "auto",
                display: "flex",
                color: colors.soft,
              }}
            >
              {expanded ? (
                <ExpandLess sx={{ fontSize: 20 }} />
              ) : (
                <ExpandMore sx={{ fontSize: 20 }} />
              )}
            </Box>
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const SubItem = ({ label, onClick }) => {
    return (
      <ListItem disablePadding sx={{ px: open || isMobile ? 1 : 0.75, mb: 0.15 }}>
        <ListItemButton
          onClick={onClick}
          sx={{
            minHeight: open || isMobile ? 35 : 44,
            px: open || isMobile ? 2 : 0.45,
            py: 0.55,
            borderRadius: 1.6,
            bgcolor: "transparent",
            borderLeft: open || isMobile ? `2px solid ${colors.border}` : "none",
            justifyContent: open || isMobile ? "flex-start" : "center",
            transition: "background-color .18s ease, border-color .18s ease",
            "&:hover": {
              bgcolor: colors.submenuHover,
              borderLeftColor: open || isMobile ? colors.muted : "transparent",
            },
          }}
        >
          <SubText label={label} />
        </ListItemButton>
      </ListItem>
    );
  };

  const SubMenuWrapper = ({ children, opened }) => {
    return (
      <Collapse in={opened} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mx: open || isMobile ? 1 : 0.65,
            my: 0.35,
            py: 0.45,
            borderRadius: 2,
            bgcolor: colors.submenu,
            border: `1px solid ${colors.border}`,
          }}
        >
          <List component="div" disablePadding>
            {children}
          </List>
        </Box>
      </Collapse>
    );
  };

  if (!hasAccess) return null;

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? open : true}
      onClose={() => setOpen(false)}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isMobile ? 0 : drawerWidth,
        flexShrink: 0,
        position: "relative",
        zIndex: isMobile ? 1500 : 1000,
        "& .MuiDrawer-paper": {
          width: isMobile ? 282 : drawerWidth,
          boxSizing: "border-box",
          transition: "width 0.25s ease",
          bgcolor: colors.base,
          color: colors.text,
          overflowX: "hidden",
          borderRight: `1px solid ${colors.border}`,
          display: "flex",
          justifyContent: "flex-start",
          boxShadow: "none",
          zIndex: isMobile ? 1500 : 1000,
          top: isMobile ? 0 : 0,
          height: "100vh",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          minHeight: "100%",
          bgcolor: colors.base,
          pt: isMobile ? 1.2 : 1,
          pb: 1,
        }}
      >
        <List sx={{ width: "100%", py: 0 }}>
          {canSeeAccounting && (
            <>
              <MainItem
                icon={<AccountBalance />}
                label="Contabilidad"
                onClick={handleToggleAccounting}
                active={accountingOpen}
                expandable
                expanded={accountingOpen}
              />

              <SubMenuWrapper opened={accountingOpen}>
                <SubItem
                  label="Plan de Cuentas"
                  onClick={() => handleNavigate("/admin-panel/admin-cuentas")}
                />
              </SubMenuWrapper>
            </>
          )}

          <MainItem
            icon={<Inventory />}
            label="Secciones"
            onClick={() => handleNavigate("/admin-panel/secciones")}
          />

          <MainItem
            icon={<Storefront />}
            label="Inventario"
            onClick={handleToggleInventory}
            active={submenuOpen}
            expandable
            expanded={submenuOpen}
          />

          <SubMenuWrapper opened={submenuOpen}>
            <SubItem
              label="Productos"
              onClick={() => handleNavigate("/admin-panel/inventario/productos")}
            />

            <SubItem
              label="Bodegas"
              onClick={() => handleNavigate("/admin-panel/inventario/bodegas")}
            />

            <SubItem
              label="Variantes"
              onClick={() => handleNavigate("/admin-panel/inventario/variantes")}
            />

            <SubItem
              label="Movimientos"
              onClick={() =>
                handleNavigate("/admin-panel/inventario/movimientos")
              }
            />

            <SubItem
              label="Servicios"
              onClick={() => handleNavigate("/admin-panel/servicios")}
            />
          </SubMenuWrapper>

          <MainItem
            icon={<Description />}
            label="Proformas"
            onClick={() => handleNavigate("/admin-panel/proformas")}
          />

          <MainItem
            icon={<People />}
            label="Personas"
            onClick={() => handleNavigate("/admin-panel/clientes")}
          />

          <MainItem
            icon={<LocalOffer />}
            label="Promociones"
            onClick={() => handleNavigate("/admin-panel/promociones")}
          />

          {canSeeAccounting && (
            <MainItem
              icon={<AttachMoneyIcon />}
              label="Bancos"
              onClick={() => handleNavigate("/admin-panel/bancos")}
            />
          )}
        </List>
      </Box>
    </Drawer>
  );
};

export default SidebarMenu;